import React, { useMemo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { anodum, i18next, view } from '@yola/ws-sdk';
import withSelection from 'src/js/modules/utils/with-selection';
import highlighter from 'src/js/modules/highlighter';
import selectors from '../../../common/constants/selectors';
import getCompositeItemOptions from '../helpers/get-сomposite-item-options';
import trackSelectLayerElement from '../trackers/track-select-layer-element';
import trackCompositeGroupAddWidgetClicked from '../trackers/track-composite-group-add-widget-clicked';
import addCompositeItem from '../helpers/add-composite-item';

const { SimpleSelect } = designSystem;

const MENU_WIDTH = '185px';
const ADD_WIDGET_VALUE = 'add-widget';

const LayerSelect = ({ elementId, getContext }) => {
  const [childrenChangeCount, setChildrenChangeCount] = useState(0);
  const compositeItemLiveNode = view.accessors.getLiveElement(elementId);

  let targetLiveNode = compositeItemLiveNode;

  if (anodum.isTag(compositeItemLiveNode, selectors.COMPOSITE_ITEM)) {
    targetLiveNode = compositeItemLiveNode.closest(selectors.COMPOSITE_GROUP);
  }

  const options = useMemo(
    () => {
      const result = getCompositeItemOptions(targetLiveNode);
      result.push({
        options: [
          {
            value: ADD_WIDGET_VALUE,
            icon: {
              glyph: 'plus-for-button',
              size: 20,
            },
            label: `${i18next.t('Add widget')}`,
          },
        ],
      });

      return result;
    },
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    [targetLiveNode, childrenChangeCount] // Recalculate options when children change to ensure the select is updated
  );

  const defaultOption = useMemo(() => {
    if (anodum.isTag(compositeItemLiveNode, selectors.COMPOSITE_GROUP)) {
      const groupElementRefId = view.accessors.getLiveElementId(compositeItemLiveNode);

      return options.find((option) => option.value === groupElementRefId);
    }

    const elementRefId = view.accessors.getLiveElementId(compositeItemLiveNode.firstElementChild);
    return options.find((option) => option.value === elementRefId);
  }, [compositeItemLiveNode, options]);

  const handleChange = useCallback(
    ({ value, label }) => {
      if (value === ADD_WIDGET_VALUE) {
        const { controlPane } = getContext();

        controlPane.hidePane();
        highlighter.operations.hide();
        addCompositeItem({ elementId });
        trackCompositeGroupAddWidgetClicked({ elementId });
        return;
      }

      // Force programmatic selection (not user-triggered) to reopen all control pane triggers when changing layers.
      // And since this action also tracks the `SITE_ELEMENT_CLICKED` event,
      // mark it as `forced` to avoid firing that event.
      view.operations.setSelectedElement(value, { forced: true });

      trackSelectLayerElement({ elementId, layerElementType: label });
    },
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    [elementId]
  );

  useEffect(() => {
    if (!targetLiveNode) return Function.prototype;

    const observer = new MutationObserver(() => {
      setChildrenChangeCount((count) => count + 1);
    });

    observer.observe(targetLiveNode, { childList: true });

    return () => observer.disconnect();
  }, [targetLiveNode]);

  return (
    <div className="ws-composite-item-layer-select">
      <SimpleSelect
        key={childrenChangeCount}
        options={options}
        defaultValue={defaultOption}
        size="medium"
        menuWidth={MENU_WIDTH}
        onChange={handleChange}
      />
    </div>
  );
};

LayerSelect.propTypes = {
  elementId: PropTypes.string.isRequired,
  getContext: PropTypes.func.isRequired,
};

export default withSelection(LayerSelect);
